import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface UserProfileData {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company_name?: string;
  cuit_dni?: string;
  city?: string;
  client_type?: 'small' | 'medium' | 'large' | 'admin' | string;
  role?: string;
  created_at?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  userProfile: UserProfileData | null;
  isAdmin: boolean;
  isPasswordRecovery: boolean;
  setIsPasswordRecovery: (value: boolean) => void;
  login: (token: string, userData: any) => Promise<{ success: boolean; error?: Error; profile?: UserProfileData }>;
  logout: () => Promise<void>;
  hasRole: (roles: string[]) => boolean;
  refreshProfile: () => Promise<UserProfileData | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getCachedProfile = (): UserProfileData | null => {
  try {
    const saved = localStorage.getItem('userProfile');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const getCachedUser = (): any => {
  try {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const createFallbackProfile = (userData: any, existingProfile?: UserProfileData | null): UserProfileData => {
  const meta = userData?.user_metadata || {};
  const emailName = userData?.email ? userData.email.split('@')[0] : 'Usuario';
  const clientType = meta.client_type || (meta.role === 'admin' ? 'admin' : (existingProfile?.client_type || 'small'));
  const role = meta.role || existingProfile?.role || (clientType === 'admin' ? 'admin' : 'client');
  return {
    id: userData?.id || existingProfile?.id || '',
    name: meta.name || meta.full_name || existingProfile?.name || emailName,
    email: userData?.email || existingProfile?.email || '',
    client_type: clientType,
    role: role,
    phone: meta.phone || existingProfile?.phone || '',
    company_name: meta.company_name || existingProfile?.company_name || ''
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(() => getCachedProfile());
  const [user, setUser] = useState<any>(() => getCachedUser());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('token') || localStorage.getItem('userProfile') || getCachedProfile());
  });
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isLoading, setIsLoading] = useState(() => {
    // Si ya tenemos perfil en localStorage, no bloqueamos con isLoading inicial
    return !Boolean(getCachedProfile() || localStorage.getItem('userProfile'));
  });

  const fetchUserProfile = async (userId: string, userData?: any): Promise<UserProfileData> => {
    const cached = getCachedProfile();
    const fallback = createFallbackProfile(userData || { id: userId }, cached || userProfile);
    if (!userId) return fallback;

    try {
      // Consultamos únicamente las columnas existentes en la tabla profiles de Supabase (id, name, client_type, created_at)
      const queryPromise = supabase
        .from('profiles')
        .select('id, name, client_type, created_at')
        .eq('id', userId)
        .maybeSingle();

      const timeoutPromise = new Promise<{ data: any; error: any }>((resolve) =>
        setTimeout(() => resolve({ data: null, error: new Error('Query timeout') }), 3000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error || !data) {
        return fallback;
      }

      const activeClientType = data.client_type || fallback.client_type || 'small';
      const activeRole = activeClientType.toLowerCase() === 'admin' ? 'admin' : (fallback.role || 'client');

      const mergedProfile: UserProfileData = {
        ...fallback,
        id: data.id || fallback.id,
        name: data.name || fallback.name,
        email: userData?.email || fallback.email,
        client_type: activeClientType,
        role: activeRole,
        phone: fallback.phone,
        company_name: fallback.company_name
      };

      return mergedProfile;
    } catch (error) {
      console.warn('Error fetching profile from db, using fallback:', error);
      return fallback;
    }
  };

  const refreshProfile = useCallback(async (): Promise<UserProfileData | null> => {
    if (!user?.id) return null;
    const profile = await fetchUserProfile(user.id, user);
    if (profile) {
      setUserProfile(profile);
      localStorage.setItem('userProfile', JSON.stringify(profile));
    }
    return profile;
  }, [user]);

  const login = async (token: string, userData: any) => {
    try {
      console.log('🔍 Login initiated for user:', userData.id);
      
      const profile = await fetchUserProfile(userData.id, userData);
      
      setUser(userData);
      setUserProfile(profile);
      setIsAuthenticated(true);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userProfile', JSON.stringify(profile));
      
      console.log('✅ Login successful:', profile);
      return { success: true, profile };
    } catch (error: any) {
      console.error('❌ Login error details:', error);
      // Aun en caso de error, si tenemos el usuario, autenticar con fallback
      const fallback = createFallbackProfile(userData, userProfile);
      setUser(userData);
      setUserProfile(fallback);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('userProfile', JSON.stringify(fallback));
      return { success: true, profile: fallback };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('Error during signOut:', err);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userProfile');
    setIsAuthenticated(false);
    setUser(null);
    setUserProfile(null);
    setIsPasswordRecovery(false);
  };

  const isAdmin = Boolean(
    (userProfile && (
      String(userProfile.client_type || '').toLowerCase() === 'admin' || 
      String(userProfile.client_type || '').toLowerCase() === 'administrador' ||
      String(userProfile.role || '').toLowerCase() === 'admin' ||
      String(userProfile.role || '').toLowerCase() === 'administrador'
    )) || (
      String(user?.user_metadata?.client_type || '').toLowerCase() === 'admin' ||
      String(user?.user_metadata?.client_type || '').toLowerCase() === 'administrador' ||
      String(user?.user_metadata?.role || '').toLowerCase() === 'admin' ||
      String(user?.user_metadata?.role || '').toLowerCase() === 'administrador'
    )
  );

  const hasRole = (roles: string[]): boolean => {
    if (!userProfile && !user) return false;
    const clientType = (userProfile?.client_type || user?.user_metadata?.client_type || '').toLowerCase();
    const role = (userProfile?.role || user?.user_metadata?.role || '').toLowerCase();
    const lowerRoles = roles.map(r => r.toLowerCase());
    return lowerRoles.includes(clientType) || lowerRoles.includes(role) || (isAdmin && lowerRoles.includes('admin'));
  };

  useEffect(() => {
    // 1. Detectar si la URL contiene parámetros de recuperación o errores
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const fullUrlParams = hash + search;

    if (fullUrlParams.includes('type=recovery') || fullUrlParams.includes('error_description') || window.location.pathname === '/reset-password') {
      console.log('🔑 Password recovery detected in URL');
      setIsPasswordRecovery(true);
    }

    // 2. Inicializar sesión actual
    const initAuth = async () => {
      try {
        // Si hay access_token y refresh_token en el hash de recuperación, establecer la sesión
        if (hash) {
          const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          if (accessToken && refreshToken) {
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            setIsPasswordRecovery(true);
          }
        }

        // Si hay código PKCE en query params, intercambiar por sesión
        if (search) {
          const searchParams = new URLSearchParams(search);
          const code = searchParams.get('code');
          if (code) {
            await supabase.auth.exchangeCodeForSession(code);
            setIsPasswordRecovery(true);
          }
        }

        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setUser(session.user);
          localStorage.setItem('user', JSON.stringify(session.user));
          const profile = await fetchUserProfile(session.user.id, session.user);
          setUserProfile(profile);
          setIsAuthenticated(true);
          localStorage.setItem('userProfile', JSON.stringify(profile));
        } else {
          // Solo desautenticar si no había sesión en local
          const savedToken = localStorage.getItem('token');
          if (!savedToken) {
            setIsAuthenticated(false);
            setUser(null);
            setUserProfile(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // 3. Suscribirse a cambios de estado de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
          localStorage.setItem('user', JSON.stringify(session.user));
          const profile = await fetchUserProfile(session.user.id, session.user);
          setUserProfile(profile);
          setIsAuthenticated(true);
          localStorage.setItem('userProfile', JSON.stringify(profile));
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
        setIsPasswordRecovery(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated,
      isLoading,
      user, 
      userProfile, 
      isAdmin,
      isPasswordRecovery,
      setIsPasswordRecovery,
      login, 
      logout, 
      hasRole,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
